<script setup>
import {
  ref,
  computed,
  onMounted,
  onBeforeUnmount,
  watch,
  nextTick,
} from 'vue';
import { useStore } from 'vuex';
import { vOnClickOutside } from '@vueuse/components';
import { useRoute, useRouter } from 'vue-router';
import { useAlert } from 'dashboard/composables';
import { useMapGetter } from 'dashboard/composables/store.js';
import { useAdmin } from 'dashboard/composables/useAdmin';
import { useSnakeCase } from 'dashboard/composables/useTransformKeys';
import ConversationFunnelMappingsAPI from 'dashboard/api/conversationFunnelMappings';
import KanbanAPI, { cancelPendingKanbanRequests } from 'dashboard/api/kanban';
import KanbanBoard from './components/kanban/KanbanBoard.vue';
import KanbanSidePanel from './components/kanban/KanbanSidePanel.vue';
import ConversationModal from './components/kanban/ConversationModal.vue';
import CrmModal from './components/kanban/CrmModal.vue';
import ChatTypeTabs from 'dashboard/components/widgets/ChatTypeTabs.vue';
import ConversationBasicFilter from 'dashboard/components/widgets/conversation/ConversationBasicFilter.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import ConversationFilter from 'next/filter/ConversationFilter.vue';
import { useFunnelManager, softResetFunnelManagerState } from 'dashboard/composables/kanban/useFunnelManager';
import { useMutexLock } from 'dashboard/composables/kanban/useMutexLock';
import { useKanbanStorage } from 'dashboard/composables/kanban/useKanbanStorage';
import wootConstants from 'dashboard/constants/globals';

// Props
const props = defineProps({
  inboxId: {
    type: [String, Number],
    default: 0,
  },
});
const store = useStore();
const route = useRoute();
const router = useRouter();

// Estado local
const selectedConversationId = ref(null);
const showConversationModal = ref(false);
const showCrmModal = ref(false);
const selectedContactId = ref(null);
const selectedContactConversationId = ref(null);

// Estado do painel lateral
const showSidePanel = ref(false);
const selectedConversation = ref(null);
const isLoadingFunnel = ref(false);
const isInitializing = ref(true);
const showAdvancedFilters = ref(false);
const appliedFilter = ref([]);
const currentAdvancedFilters = ref([]); // Filtros avançados para API do Kanban

// Estado para conversas carregadas via API otimizada do Kanban
const kanbanStagesData = ref(null);
const isLoadingKanban = ref(false);
const kanbanLoadError = ref(null);

// Bug #31 Fix: AbortController for canceling pending requests on unmount
let abortController = null;
// Bug #31 Fix: Track if component is mounted to prevent state updates after unmount
const isMounted = ref(true);

// Estados do novo header estilo concorrente
const searchQuery = ref('');
const showFunnelDropdown = ref(false);
const showSettingsDropdown = ref(false);
const funnelTab = ref('active');
const funnelSearchQuery = ref('');

// Estados dos filtros nativos
const activeAssigneeTab = ref(wootConstants.ASSIGNEE_TYPE.ME);
const activeStatus = ref(wootConstants.STATUS_TYPE.ALL); // Filtro padrão: Todas
const activeSortBy = ref(wootConstants.SORT_BY_TYPE.LAST_ACTIVITY_AT_DESC);

// Getters nativos do store
const mineChatsList = useMapGetter('getMineChats');
const allChatList = useMapGetter('getAllStatusChats');
const unAssignedChatsList = useMapGetter('getUnAssignedChats');
const chatLists = useMapGetter('getFilteredConversations');
const currentUser = useMapGetter('getCurrentUser');
const appliedFilters = useMapGetter('getAppliedConversationFilters');

// Composables de permissão
const { isAdmin } = useAdmin();

// Computed para agentes visíveis no header
const allAgents = computed(() => {
  return store.getters['agents/getAgents'] || [];
});

const visibleAgents = computed(() => {
  return allAgents.value.slice(0, 5);
});

const additionalAgentsCount = computed(() => {
  return Math.max(0, allAgents.value.length - 5);
});

// Computed para funis filtrados no dropdown
const filteredFunnels = computed(() => {
  let list = funnels.value || [];

  // Filtrar por tab (ativos/arquivados)
  if (funnelTab.value === 'archived') {
    list = list.filter(f => f.archived === true);
  } else {
    list = list.filter(f => !f.archived);
  }

  // Filtrar por busca
  if (funnelSearchQuery.value) {
    const query = funnelSearchQuery.value.toLowerCase();
    list = list.filter(f => f.name?.toLowerCase().includes(query));
  }

  return list;
});

// Métodos do novo header
const selectFunnelFromDropdown = async funnelId => {
  showFunnelDropdown.value = false;
  await handleFunnelSelection(funnelId);
};

const handleGerenciarFunil = () => {
  showSettingsDropdown.value = false;
  router.push({ name: 'manage_funnels' });
};


const handleGerenciarPermissoes = () => {
  showSettingsDropdown.value = false;

  // Navegar para a página de configurações do funil atual
  if (currentFunnel.value?.id) {
    router.push({
      name: 'funnel_settings',
      params: {
        accountId: store.getters.getCurrentAccountId,
        funnelId: currentFunnel.value.id,
      },
    });
  } else {
    useAlert('Selecione um funil primeiro para configurar permissões');
  }
};

// Verificar se é super admin
const isSuperAdmin = computed(() => {
  return currentUser.value?.type === 'SuperAdmin';
});

// Definir tab inicial - TODOS começam com "Todas" por padrão
watch(
  [isSuperAdmin, isAdmin],
  () => {
    // TODOS (admin e agente) começam com "Todas"
    // A diferença está no que "Todas" retorna no backend:
    // - Admin: vê todas as conversas mesmo
    // - Agente: vê apenas conversas dele + não atribuídas
    activeAssigneeTab.value = wootConstants.ASSIGNEE_TYPE.ALL;
  },
  { immediate: true }
);

// Helper para obter ícone do canal (moved before use)
const getChannelIcon = (channelType, provider) => {
  const channelTypeMap = {
    'Channel::Api': 'ri-cloudy-fill',
    'Channel::Email': 'ri-mail-fill',
    'Channel::FacebookPage': 'ri-messenger-fill',
    'Channel::Telegram': 'ri-telegram-fill',
    'Channel::TwitterProfile': 'ri-twitter-x-fill',
    'Channel::WebWidget': 'ri-global-fill',
    'Channel::Whatsapp': 'ri-whatsapp-fill',
    'Channel::Instagram': 'ri-instagram-fill',
    'Channel::Voice': 'ri-phone-fill',
    api: 'ri-cloudy-fill',
    email: 'ri-mail-fill',
    facebook: 'ri-messenger-fill',
    telegram: 'ri-telegram-fill',
    twitter: 'ri-twitter-x-fill',
    web_widget: 'ri-global-fill',
    whatsapp: 'ri-whatsapp-fill',
    instagram: 'ri-instagram-fill',
    voice: 'ri-phone-fill',
  };

  // Se for email, verificar provider
  if (
    (channelType === 'Channel::Email' || channelType === 'email') &&
    provider
  ) {
    if (provider === 'microsoft') return 'ri-microsoft-fill';
    if (provider === 'google') return 'ri-google-fill';
  }

  return channelTypeMap[channelType] || 'ri-global-fill';
};

// Helper para obter cor do canal (moved before use)
const getChannelColor = icon => {
  if (icon.includes('whatsapp')) return 'text-green-500';
  if (icon.includes('messenger') || icon.includes('facebook'))
    return 'text-blue-500';
  if (icon.includes('instagram')) return 'text-pink-500';
  if (icon.includes('telegram')) return 'text-cyan-500';
  if (icon.includes('twitter')) return 'text-sky-500';
  if (icon.includes('mail')) return 'text-amber-500';
  if (icon.includes('microsoft')) return 'text-blue-600';
  if (icon.includes('google')) return 'text-red-500';
  if (icon.includes('phone')) return 'text-purple-500';
  return 'text-slate-500';
};

// Composables - usar selectedFunnelId do composable (moved before use)
const {
  funnels,
  currentFunnel,
  selectedFunnelId,
  loadFunnels,
  selectFunnel,
  createFunnelFromTemplateId,
} = useFunnelManager();

// Bug #3 Fix: Mutex lock para prevenir race conditions
// Bug #31 Fix: Added forceReleaseLocks for cleanup on unmount
const { acquireLock, isLocked, forceReleaseLocks } = useMutexLock();

// Bug #16 & #19 Fix: Storage com TTL, cleanup automático e lock para prevenir race conditions
const kanbanStorage = useKanbanStorage();

// Filtros da conversa usando APENAS parâmetros nativos do Chatwoot (moved before use)
// PERFORMANCE FIX: Removido disablePagination - agora usamos API otimizada do Kanban
const conversationFilters = computed(() => {
  return {
    inboxId:
      props.inboxId && props.inboxId !== 0
        ? parseInt(props.inboxId, 10)
        : undefined,
    assigneeType: activeAssigneeTab.value,
    status: activeStatus.value,
    sortBy: activeSortBy.value,
    page: 1,
  };
});

// Verifica se tem filtros aplicados (considera tanto o store quanto os filtros locais do Kanban)
const hasAppliedFilters = computed(() => {
  const storeFilters = appliedFilters.value && appliedFilters.value.length > 0;
  const kanbanFilters =
    currentAdvancedFilters.value && currentAdvancedFilters.value.length > 0;
  return storeFilters || kanbanFilters;
});

// Lista de conversas usando a lógica NATIVA do Chatwoot (moved before use)
const conversationList = computed(() => {
  let localConversationList = [];

  if (!hasAppliedFilters.value) {
    // Usar filtros básicos nativos do Chatwoot
    const filters = conversationFilters.value;

    // USAR OS GETTERS NATIVOS DO CHATWOOT
    if (activeAssigneeTab.value === wootConstants.ASSIGNEE_TYPE.ME) {
      localConversationList = [...mineChatsList.value(filters)];
    } else if (
      activeAssigneeTab.value === wootConstants.ASSIGNEE_TYPE.UNASSIGNED
    ) {
      localConversationList = [...unAssignedChatsList.value(filters)];
    } else if (activeAssigneeTab.value === wootConstants.ASSIGNEE_TYPE.ALL) {
      localConversationList = [...allChatList.value(filters)];
    }
  } else {
    // Usar filtros avançados aplicados
    localConversationList = [...chatLists.value];
  }

  // Filtrar por funil atual usando funnel_mappings (múltiplos funis) ou custom_attributes (compatibilidade)
  if (currentFunnel.value) {
    const funnelId = currentFunnel.value.id;
    localConversationList = localConversationList.filter(c => {
      // Primeiro verifica funnel_mappings (múltiplos funis)
      if (c.funnel_mappings && c.funnel_mappings.length > 0) {
        return c.funnel_mappings.some(
          mapping => String(mapping.funnel_id) === String(funnelId)
        );
      }

      // Fallback para custom_attributes (compatibilidade)
      const customFunnelId = c.custom_attributes?.funnel_id;
      return customFunnelId && String(customFunnelId) === String(funnelId);
    });
  }

  return localConversationList;
});

// Computed para obter canais únicos das conversas atuais (moved after conversationList)
const activeChannels = computed(() => {
  const channels = new Map();
  const inboxes = store.getters['inboxes/getInboxes'] || [];

  // Mostrar todos os canais das conversas no funil, sem restrição
  conversationList.value.forEach(conv => {
    const inbox = inboxes.find(i => i.id === conv.inbox_id);
    if (inbox) {
      const channelType = inbox.channel_type || inbox.channel;
      const icon = getChannelIcon(channelType, inbox.provider);
      const color = getChannelColor(icon);

      if (!channels.has(channelType)) {
        channels.set(channelType, {
          type: channelType,
          icon,
          color,
          count: 0,
          inboxes: [],
        });
      }

      const channel = channels.get(channelType);
      channel.count += 1;

      if (!channel.inboxes.includes(inbox.name)) {
        channel.inboxes.push(inbox.name);
      }
    }
  });

  return Array.from(channels.values());
});

// Computed para contar conversas por tab no funil atual
const getFunnelFilteredCount = type => {
  if (!currentFunnel.value) {
    return 0;
  }

  const funnelId = currentFunnel.value.id;
  const filters = conversationFilters.value;

  let conversations = [];
  if (type === wootConstants.ASSIGNEE_TYPE.ME) {
    conversations = mineChatsList.value(filters);
  } else if (type === wootConstants.ASSIGNEE_TYPE.UNASSIGNED) {
    conversations = unAssignedChatsList.value(filters);
  } else if (type === wootConstants.ASSIGNEE_TYPE.ALL) {
    conversations = allChatList.value(filters);
  }

  // Filtrar por funil usando funnel_mappings (múltiplos funis) ou custom_attributes
  const filtered = conversations.filter(c => {
    // Primeiro verifica funnel_mappings (múltiplos funis)
    if (c.funnel_mappings && c.funnel_mappings.length > 0) {
      return c.funnel_mappings.some(
        mapping => String(mapping.funnel_id) === String(funnelId)
      );
    }

    // Fallback para custom_attributes (compatibilidade)
    const customFunnelId = c.custom_attributes?.funnel_id;
    return customFunnelId && String(customFunnelId) === String(funnelId);
  });

  return filtered.length;
};

// Computed para items das tabs - TODOS veem todas as opções
const assigneeTabItems = computed(() => {
  const items = [];

  // TODOS veem "Todas" (comportamento é diferente por tipo de usuário no backend)
  // Admin: vê todas as conversas mesmo
  // Agente: vê apenas conversas dele + não atribuídas
  items.push({
    key: wootConstants.ASSIGNEE_TYPE.ALL,
    name: 'Todas',
    count: getFunnelFilteredCount(wootConstants.ASSIGNEE_TYPE.ALL),
  });

  // Todos veem "Minhas"
  items.push({
    key: wootConstants.ASSIGNEE_TYPE.ME,
    name: 'Minhas',
    count: getFunnelFilteredCount(wootConstants.ASSIGNEE_TYPE.ME),
  });

  // Todos veem "Não atribuídas"
  items.push({
    key: wootConstants.ASSIGNEE_TYPE.UNASSIGNED,
    name: 'Não atribuídas',
    count: getFunnelFilteredCount(wootConstants.ASSIGNEE_TYPE.UNASSIGNED),
  });

  return items;
});

// Bug #7 Fix: Use Vuex as single source of truth
// modifiedStages is now ONLY a temporary optimistic update cache
// All persistent data is in funnel_mappings from backend
const modifiedStages = ref({});

// Track which conversations have pending optimistic updates
const optimisticUpdates = new Map();

// Bug #7 & #16 & #19 Fix: Funções auxiliares para persistência refatoradas
const loadSavedStages = () => {
  const accountId = store.getters.getCurrentAccountId;
  if (!accountId) return {};

  // Bug #19 Fix: Usar useKanbanStorage com TTL e cleanup
  const saved = kanbanStorage.getItem('conversation_stages', accountId);
  return saved || {};
};

const saveStages = async stages => {
  // Bug #16 Fix: Usar mutex lock para prevenir race condition no localStorage
  const release = await acquireLock('saveStages', 5000);

  try {
    const accountId = store.getters.getCurrentAccountId;
    if (!accountId) return;

    // Carregar stages existentes de TODOS os funis
    const existingStages = loadSavedStages();

    // Atualizar apenas os stages do funil atual
    const enrichedStages = { ...existingStages };

    Object.entries(stages).forEach(([convId, stage]) => {
      enrichedStages[convId] = {
        stage,
        funnel_id: currentFunnel.value?.id,
        timestamp: Date.now(),
        updated_at: new Date().toISOString(),
      };
    });

    // Bug #19 Fix: Usar useKanbanStorage com TTL (7 dias)
    kanbanStorage.setItem('conversation_stages', enrichedStages, accountId);

    // Bug #19 Fix: Executar cleanup periódico
    kanbanStorage.cleanupOldData(accountId);
  } finally {
    // Bug #16 Fix: Sempre liberar lock
    release();
  }
};

// Computed para calcular o valor total geral de todas as conversas visíveis DO FUNIL ATUAL
const totalGeneralValue = computed(() => {
  let total = 0;
  const currentFunnelId = currentFunnel.value?.id;

  // Se não há funil selecionado, retorna 0
  if (!currentFunnelId) return 0;

  // Usar dados da API otimizada do Kanban se disponíveis
  if (kanbanStagesData.value?.stages) {
    kanbanStagesData.value.stages.forEach(stage => {
      const conversations = stage.conversations || [];
      conversations.forEach(conversation => {
        const products = conversation.conversation_products || [];

        // Filtrar produtos pelo funnel_id atual
        const filteredProducts = products.filter(
          product => !product.funnel_id || product.funnel_id === currentFunnelId
        );

        filteredProducts.forEach(product => {
          total += parseFloat(product.amount) || 0;
        });
      });
    });
    return total;
  }

  // Fallback: usar conversationList
  conversationList.value.forEach(conversation => {
    const products = conversation.conversation_products || [];

    // Filtrar produtos pelo funnel_id atual
    // Incluir produtos sem funnel_id (null) ou do funil atual
    const filteredProducts = products.filter(
      product => !product.funnel_id || product.funnel_id === currentFunnelId
    );

    filteredProducts.forEach(product => {
      total += parseFloat(product.amount) || 0;
    });
  });

  return total;
});

// Formatar valor total geral
const formattedTotalGeneralValue = computed(() => {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(totalGeneralValue.value);
});

// Computed para contar total de cards no Kanban
const totalCardsCount = computed(() => {
  // Se temos dados da API otimizada, usar o total de lá
  if (kanbanStagesData.value?.stages) {
    return kanbanStagesData.value.stages.reduce((total, stage) => {
      return total + (stage.total_count || stage.conversations?.length || 0);
    }, 0);
  }
  // Fallback: usar conversationList
  return conversationList.value.length;
});

// Computed para cor do estágio da conversa selecionada (para o SidePanel)
const selectedConversationStageColor = computed(() => {
  if (!selectedConversation.value || !currentFunnel.value?.stages) {
    return '#6366f1'; // Cor padrão
  }

  // Obter o stage_name da conversa selecionada
  const mapping = selectedConversation.value.funnel_mappings?.[0];
  const stageName =
    mapping?.stage_name ||
    modifiedStages.value[selectedConversation.value.id] ||
    selectedConversation.value.custom_attributes?.funnel_stage;

  if (!stageName) return '#6366f1';

  // Buscar a cor do estágio no funil atual
  const stageConfig = currentFunnel.value.stages[stageName];
  return stageConfig?.color || '#6366f1';
});

// Computed style for funnel select dropdown (to avoid inline styles)
const funnelSelectStyles = {
  height: '32px',
  lineHeight: '1',
  minWidth: '200px',
  padding: '0 40px 0 12px',
  margin: '0',
  border: '1px solid var(--n-weak)',
  backgroundColor: 'var(--n-solid-1)',
  borderRadius: '6px',
  appearance: 'none',
  outline: 'none',
  boxShadow: 'none',
  fontSize: '14px',
  backgroundImage:
    "url('data:image/svg+xml,%3csvg xmlns=%27http://www.w3.org/2000/svg%27 fill=%27none%27 viewBox=%270 0 20 20%27%3e%3cpath stroke=%27%236b7280%27 stroke-linecap=%27round%27 stroke-linejoin=%27round%27 stroke-width=%271.5%27 d=%27M6 8l4 4 4-4%27/%3e%3c/svg%3e')",
  backgroundPosition: 'right 0.5rem center',
  backgroundRepeat: 'no-repeat',
  backgroundSize: '1.25em 1.25em',
  transition: 'border-color 0.2s',
};

// Computed para conversas com todos os filtros aplicados
const conversations = computed(() => {
  // Usar conversationList que já aplica todos os filtros necessários
  let filteredConversations = conversationList.value;

  // Adicionar informações do funil às conversas
  if (currentFunnel.value && currentFunnel.value.id) {
    filteredConversations = filteredConversations.map(c => {
      // Verificar se este conversa teve seu stage modificado
      const modifiedStage = modifiedStages.value[c.id];

      // Buscar o stage_name do mapping específico para este funil
      let stageName = null;

      // Primeiro tenta buscar dos funnel_mappings (múltiplos funis)
      if (c.funnel_mappings && c.funnel_mappings.length > 0) {
        const mapping = c.funnel_mappings.find(
          m => String(m.funnel_id) === String(currentFunnel.value.id)
        );
        if (mapping) {
          stageName = mapping.stage_name;
        }
      }

      // Se não encontrou nos mappings, usa custom_attributes (compatibilidade)
      if (
        !stageName &&
        c.custom_attributes?.funnel_id === String(currentFunnel.value.id)
      ) {
        stageName = c.custom_attributes?.stage_name;
      }

      // Aplica modificações locais ou usa o stage encontrado ou default
      stageName =
        modifiedStage ||
        stageName ||
        currentFunnel.value.settings?.default_stage ||
        Object.keys(currentFunnel.value.stages)[0] ||
        'cadastro';

      return {
        ...c,
        funnel_id: currentFunnel.value.id,
        funnel: currentFunnel.value, // Adicionar o objeto funnel completo para os cards terem acesso às configurações
        stage_name: stageName,
      };
    });
  }

  // Se tiver inbox selecionado adicionalmente, filtrar por inbox
  if (props.inboxId && props.inboxId !== 0) {
    filteredConversations = filteredConversations.filter(
      c => c.inbox_id === parseInt(props.inboxId, 10)
    );
  }

  // Aplicar ordenação se especificada
  if (activeSortBy.value && activeSortBy.value !== 'last_activity_at_desc') {
    filteredConversations = [...filteredConversations].sort((a, b) => {
      const priorityOrder = { urgent: 0, high: 1, medium: 2, low: 3 };

      switch (activeSortBy.value) {
        case 'priority_desc': {
          return (
            (priorityOrder[a.priority] || 999) -
            (priorityOrder[b.priority] || 999)
          );
        }
        case 'created_at_desc': {
          return new Date(b.created_at) - new Date(a.created_at);
        }
        default: {
          return (
            new Date(b.last_activity_at || b.created_at) -
            new Date(a.last_activity_at || a.created_at)
          );
        }
      }
    });
  }

  return filteredConversations;
});

// Removido - funis não dependem mais de inboxes
// const hasConfiguredInboxes = computed(() => {
//   const inboxIds = currentFunnel.value?.settings?.inbox_ids || [];
//   return inboxIds.length > 0;
// });

const groupedConversations = computed(() => {
  if (!currentFunnel.value) return {};

  // REACTIVITY FIX: Access modifiedStages.value directly to create dependency
  // This ensures the computed re-runs when any stage is modified
  const currentModifiedStages = { ...modifiedStages.value };

  const grouped = {};
  // Inicializar todos os estágios com arrays vazios e metadata
  Object.keys(currentFunnel.value.stages).forEach(stage => {
    grouped[stage] = [];
  });

  // PERFORMANCE: Se temos dados da API otimizada, usar eles diretamente
  if (kanbanStagesData.value?.stages) {
    // First, load all conversations into a map for quick lookup
    const allConversations = new Map();
    const stageMetadata = {};

    kanbanStagesData.value.stages.forEach(stageData => {
      const stageName = stageData.stage?.name;
      if (stageName) {
        stageMetadata[stageName] = {
          totalCount: stageData.total_count,
          hasMore: stageData.has_more,
        };
        stageData.conversations.forEach(conv => {
          allConversations.set(conv.id, {
            ...conv,
            funnel_id: currentFunnel.value.id,
            funnel: currentFunnel.value,
            stage_name: stageName, // Original stage from API
          });
        });
      }
    });

    // INSTANT UPDATE: Apply modifiedStages to move cards immediately
    // This is the key for instant drag-drop visual feedback
    allConversations.forEach((conv, convId) => {
      const modifiedStage = currentModifiedStages[convId];
      if (modifiedStage && modifiedStage !== conv.stage_name) {
        // Card was moved - use the new stage
        conv.stage_name = modifiedStage;
      }
    });

    // Group conversations by their current stage (considering modifications)
    allConversations.forEach(conv => {
      const stage = conv.stage_name;
      if (grouped[stage]) {
        grouped[stage].push(conv);
      }
    });

    // Add metadata to arrays
    Object.keys(grouped).forEach(stageName => {
      const meta = stageMetadata[stageName] || {
        totalCount: 0,
        hasMore: false,
      };
      Object.defineProperty(grouped[stageName], 'totalCount', {
        value: meta.totalCount,
        enumerable: false,
      });
      Object.defineProperty(grouped[stageName], 'hasMore', {
        value: meta.hasMore,
        enumerable: false,
      });
    });

    return grouped;
  }

  // FALLBACK: Usar método legado se não tiver dados da API otimizada

  // Carregar stages salvos para o funil atual
  const savedStages = loadSavedStages();

  // Agrupar conversas por estágio ESPECÍFICO DO FUNIL ATUAL
  conversations.value.forEach(conversation => {
    let stage = null;

    // 1. Primeiro verificar se a conversa já tem mapeamentos carregados
    if (
      conversation.funnel_mappings &&
      Array.isArray(conversation.funnel_mappings)
    ) {
      const mapping = conversation.funnel_mappings.find(
        m =>
          m.funnel_id === currentFunnel.value.id ||
          m.funnel_id === String(currentFunnel.value.id) ||
          String(m.funnel_id) === String(currentFunnel.value.id)
      );

      if (mapping && mapping.stage_name) {
        stage = mapping.stage_name;
      }
    }

    // 2. Se não encontrou, verificar localStorage
    if (!stage) {
      const savedData = savedStages[conversation.id];
      if (savedData && typeof savedData === 'object') {
        if (
          savedData.funnel_id === currentFunnel.value.id ||
          savedData.funnel_id === String(currentFunnel.value.id) ||
          String(savedData.funnel_id) === String(currentFunnel.value.id)
        ) {
          stage = savedData.stage;
        }
      }
    }

    // 3. Se não encontrou, verificar modifiedStages (mudanças recentes)
    if (!stage && modifiedStages.value[conversation.id]) {
      stage = modifiedStages.value[conversation.id];
    }

    // 4. Se ainda não tem stage, usar o primeiro do funil
    if (!stage) {
      stage = Object.keys(currentFunnel.value.stages)[0];
    }

    // Adicionar ao grupo correto
    if (grouped[stage]) {
      grouped[stage].push(conversation);
    } else {
      // Se o estágio não existe no funil atual, adicionar ao primeiro
      const firstStage = Object.keys(currentFunnel.value.stages)[0];
      if (firstStage) {
        grouped[firstStage].push(conversation);
      }
    }
  });

  return grouped;
});

// Methods
const handleFunnelSelection = async funnelId => {
  if (!funnelId) {
    return;
  }

  // Evitar recarregar o mesmo funil
  if (String(selectedFunnelId.value) === String(funnelId)) {
    return;
  }

  isLoadingFunnel.value = true;

  try {
    // Limpar stages modificados ao trocar de funil
    modifiedStages.value = {};

    // Usar o método selectFunnel do composable
    await selectFunnel(funnelId);

    // Aguardar um tick para garantir que o currentFunnel foi atualizado
    await nextTick();

    // Verificar se o funil foi carregado corretamente
    if (currentFunnel.value) {
      // Carregar stages salvos para este funil específico
      const allSavedStages = loadSavedStages();
      modifiedStages.value = {};

      // Filtrar apenas os stages deste funil
      Object.entries(allSavedStages).forEach(([convId, data]) => {
        if (typeof data === 'object' && data.funnel_id === funnelId) {
          modifiedStages.value[convId] = data.stage_name;
        }
      });

      // Forçar recarregamento das conversas
      await store.dispatch('fetchAllConversations');
    }
  } catch {
    // Error handled silently
  } finally {
    isLoadingFunnel.value = false;
  }
};

const handleCardClick = conversationId => {
  selectedConversationId.value = conversationId;
  showConversationModal.value = true;
};

// Handler para abrir painel lateral ao clicar no card
const handleOpenSidePanel = conversation => {
  selectedConversation.value = conversation;
  showSidePanel.value = true;
};

// Handler para fechar painel lateral
const closeSidePanel = () => {
  showSidePanel.value = false;
  selectedConversation.value = null;
};

// Handler para refresh do painel lateral
const handleSidePanelRefresh = () => {
  fetchConversations();
};

const openConversationModal = conversation => {
  if (conversation && conversation.display_id) {
    selectedConversationId.value = conversation.display_id;
    showConversationModal.value = true;
  }
};

// CRM Modal handlers
const openCrmModal = (conversation, contact) => {
  if (contact?.id) {
    selectedContactId.value = contact.id;
    selectedContactConversationId.value = conversation?.display_id || null;
    showCrmModal.value = true;
  } else if (conversation?.meta?.sender?.id) {
    selectedContactId.value = conversation.meta.sender.id;
    selectedContactConversationId.value = conversation.display_id;
    showCrmModal.value = true;
  }
};

const closeCrmModal = () => {
  showCrmModal.value = false;
  selectedContactId.value = null;
  selectedContactConversationId.value = null;
};

const handleContactUpdated = contact => {
  // Optionally refresh kanban data when contact is updated
  fetchKanbanConversations();
};

// Handler para abrir modal de novo card a partir da coluna
// Controle de tentativas para evitar loops infinitos (moved before use)
const fetchAttempts = ref(0);
const maxFetchAttempts = 3;
const fetchTimeout = ref(null);
let storeUpdateDebounceTimer = null;

// Ref para controlar chamadas de fetchConversations (moved before use)
const isFetching = ref(false);

// Debounce timers (moved before use)
let fetchDebounceTimer = null;

// PERFORMANCE FIX: Usar API otimizada do Kanban ao invés de carregar todas as conversas
const fetchKanbanConversations = async () => {
  // Bug #31 Fix: Check if component is still mounted
  if (!isMounted.value) return;

  // Proteção contra loops infinitos
  if (fetchAttempts.value >= maxFetchAttempts) {
    fetchAttempts.value = 0;
    return;
  }

  // Precisa ter um funil selecionado
  if (!currentFunnel.value?.id) {
    return;
  }

  // Bug #3 Fix: Usar mutex lock ao invés de isFetching simples
  if (isLocked('fetchConversations')) {
    return;
  }

  // Bug #31 Fix: Cancel any pending request before starting a new one
  if (abortController) {
    abortController.abort();
  }
  abortController = new AbortController();
  const currentAbortController = abortController;

  const release = await acquireLock('fetchConversations', 30000);

  // Bug #31 Fix: Check again after acquiring lock
  if (!isMounted.value) {
    release();
    return;
  }

  fetchAttempts.value += 1;
  isLoadingKanban.value = true;
  kanbanLoadError.value = null;

  try {
    // PERFORMANCE: Usar endpoint otimizado que carrega apenas 20 conversas por stage
    // Calcular assigneeId sem nested ternary
    let assigneeId;
    if (activeAssigneeTab.value === wootConstants.ASSIGNEE_TYPE.ME) {
      assigneeId = currentUser.value?.id;
    } else if (
      activeAssigneeTab.value === wootConstants.ASSIGNEE_TYPE.UNASSIGNED
    ) {
      assigneeId = 'unassigned';
    }

    // Preparar opções com filtros simples e avançados
    const options = {
      perStage: 20,
      status: activeStatus.value !== 'all' ? activeStatus.value : undefined,
      assigneeId,
      inboxId: props.inboxId && props.inboxId !== 0 ? props.inboxId : undefined,
      sortBy: 'updated_at',
      sortOrder: 'desc',
    };

    // Adicionar filtros avançados se existirem
    if (
      currentAdvancedFilters.value &&
      currentAdvancedFilters.value.length > 0
    ) {
      options.payload = currentAdvancedFilters.value;
    }

    const response = await KanbanAPI.getStageConversations(
      currentFunnel.value.id,
      options
    );

    // Bug #31 Fix: Check if request was aborted or component unmounted
    if (currentAbortController.signal.aborted || !isMounted.value) {
      return;
    }

    // STABILITY FIX: Sync modifiedStages with server data
    // Only clear modifiedStages entries that match server data
    // Keep entries for cards that have pending optimistic updates
    if (response.data?.stages) {
      const serverStages = {};
      response.data.stages.forEach(stageData => {
        const stageName = stageData.stage?.name;
        if (stageName) {
          stageData.conversations?.forEach(conv => {
            serverStages[conv.id] = stageName;
          });
        }
      });

      // Update modifiedStages to match server, but keep pending optimistic updates
      Object.keys(modifiedStages.value).forEach(convId => {
        const numericId = parseInt(convId, 10);
        if (serverStages[numericId] && !optimisticUpdates.has(numericId)) {
          // Server has this card - sync with server stage
          modifiedStages.value[convId] = serverStages[numericId];
        }
      });

      // Clear entries for cards no longer on server (unless they have optimistic updates)
      Object.keys(modifiedStages.value).forEach(convId => {
        const numericId = parseInt(convId, 10);
        if (!serverStages[numericId] && !optimisticUpdates.has(numericId)) {
          delete modifiedStages.value[convId];
        }
      });
    }

    kanbanStagesData.value = response.data;
    fetchAttempts.value = 0;
  } catch (error) {
    // Bug #31 Fix: Ignore abort errors and don't update state if unmounted
    if (
      error?.name === 'AbortError' ||
      error?.name === 'CanceledError' ||
      error?.code === 'ERR_CANCELED' ||
      !isMounted.value
    ) {
      return;
    }

    kanbanLoadError.value = error?.message || 'Erro ao carregar conversas';

    // Se for erro 500 ou 422, não tentar novamente automaticamente
    if (error?.response && (error.response.status === 500 || error.response.status === 422)) {
      fetchAttempts.value = maxFetchAttempts;
    }
  } finally {
    // Bug #31 Fix: Only update state if still mounted
    if (isMounted.value) {
      isLoadingKanban.value = false;
    }
    release();
  }
};

// Fallback: método antigo para compatibilidade (usado apenas se API otimizada falhar)
const fetchConversationsLegacy = async (forceRefresh = false) => {
  if (fetchAttempts.value >= maxFetchAttempts) {
    fetchAttempts.value = 0;
    return;
  }

  if (isLocked('fetchConversations')) {
    return;
  }

  const release = await acquireLock('fetchConversations', 30000);
  fetchAttempts.value += 1;

  try {
    if (forceRefresh) {
      await store.dispatch('emptyAllConversations');
    }

    const filters = conversationFilters.value;
    await store.dispatch('setChatListFilters', filters);
    await store.dispatch('fetchAllConversations');

    fetchAttempts.value = 0;
  } catch (error) {
    if (error.response && error.response.status === 500) {
      fetchAttempts.value = maxFetchAttempts;
    }
  } finally {
    release();
  }
};

// Função principal que tenta API otimizada primeiro
const fetchConversations = async (forceRefresh = false) => {
  // Tentar usar API otimizada do Kanban
  if (currentFunnel.value?.id) {
    await fetchKanbanConversations(forceRefresh);
  } else {
    // Fallback para método legado se não tiver funil
    await fetchConversationsLegacy(forceRefresh);
  }
};

// Handlers de filtros nativos (moved after fetchConversations)
const onBasicFilterChange = (value, type) => {
  if (type === 'status') {
    activeStatus.value = value;
  } else if (type === 'sort') {
    activeSortBy.value = value;
  }

  // Buscar conversas com os novos filtros
  // Os filtros serão automaticamente incluídos via conversationFilters computed
  fetchConversations();
};

const updateAssigneeTab = selectedTab => {
  if (activeAssigneeTab.value !== selectedTab) {
    activeAssigneeTab.value = selectedTab;
  }
};

const handleFiltersModal = () => {
  showAdvancedFilters.value = true;
  // Forçar atualização do DOM
  nextTick(() => {});
};

const closeAdvanceFiltersModal = () => {
  showAdvancedFilters.value = false;
};

const onApplyFilter = async filter => {
  appliedFilter.value = filter;

  // Criar payload com filtros - garantir que seja array
  let payload = [];
  if (Array.isArray(filter)) {
    payload = [...filter];
  } else if (filter) {
    payload = [filter];
  }

  // Garantir que o último item não tenha query_operator ou seja null
  if (payload.length > 0) {
    payload[payload.length - 1].query_operator = null;
  }

  // Converter para snake_case como o ConversationFilter original faz
  const snakeCasePayload = useSnakeCase(payload);

  // Armazenar filtros avançados para uso na API do Kanban
  currentAdvancedFilters.value = snakeCasePayload;

  // Também atualizar o store para manter compatibilidade
  store.dispatch('setChatListFilters', {
    appliedFilters: snakeCasePayload,
  });

  // Fechar modal primeiro para feedback imediato
  showAdvancedFilters.value = false;

  // Recarregar dados do Kanban com os filtros aplicados
  try {
    await fetchKanbanConversations();
  } catch {
    // Em caso de erro, limpar filtros e buscar todas
    currentAdvancedFilters.value = [];
    store.dispatch('setChatListFilters', {});
    await fetchConversations();
  }
};

// Função para limpar filtros avançados
const clearAdvancedFilters = async () => {
  appliedFilter.value = [];
  currentAdvancedFilters.value = [];
  store.dispatch('setChatListFilters', {});
  await fetchKanbanConversations();
};

// Reset filters function (commented out but kept for potential future use)
// const resetFilters = () => {
//   appliedFilter.value = [];
//   activeStatus.value = wootConstants.STATUS_TYPE.OPEN;
//   activeSortBy.value = wootConstants.SORT_BY_TYPE.LAST_ACTIVITY_AT_DESC;
//
//   store.dispatch('setChatListFilters', {});
//   fetchConversations();
// };

// PERFORMANCE: Estado para controle de paginação por stage
const stageLoadingMore = ref({});
const stageCurrentPage = ref({});

// PERFORMANCE: Carregar mais conversas para uma stage específica
const loadMoreStageConversations = async stageName => {
  if (!currentFunnel.value?.id || stageLoadingMore.value[stageName]) {
    return;
  }

  stageLoadingMore.value[stageName] = true;
  const currentPage = stageCurrentPage.value[stageName] || 1;
  const nextPage = currentPage + 1;

  try {
    const response = await KanbanAPI.loadMoreStage(
      currentFunnel.value.id,
      stageName,
      {
        page: nextPage,
        perPage: 20,
      }
    );

    // Adicionar novas conversas ao stage existente
    if (kanbanStagesData.value?.stages) {
      const stageIndex = kanbanStagesData.value.stages.findIndex(
        s => s.stage?.name === stageName
      );
      if (stageIndex !== -1) {
        const existingConvs =
          kanbanStagesData.value.stages[stageIndex].conversations || [];
        kanbanStagesData.value.stages[stageIndex].conversations = [
          ...existingConvs,
          ...response.data.conversations,
        ];
        kanbanStagesData.value.stages[stageIndex].has_more =
          response.data.has_more;
      }
    }

    stageCurrentPage.value[stageName] = nextPage;
  } catch {
    // Silently fail - user can try again
  } finally {
    stageLoadingMore.value[stageName] = false;
  }
};

// Bug #4-5 Fix: handleCardMove com mutex lock e fila de operações
const handleCardMove = async ({ conversationId, fromStage, toStage }) => {
  // Verificar se toStage é uma key válida
  const isValidKey = Object.prototype.hasOwnProperty.call(
    currentFunnel.value.stages,
    toStage
  );

  // Se não for uma key válida, pode ser uma label - vamos tentar converter
  let actualStageKey = toStage;
  if (!isValidKey) {
    // Procurar a key pela label
    const entry = Object.entries(currentFunnel.value.stages).find(
      ([, config]) => {
        return config.label === toStage || config.name === toStage;
      }
    );

    if (entry) {
      actualStageKey = entry[0];
    } else {
      // Bug #25 Fix: Exibir erro ao usuário
      useAlert('Estágio inválido');
      return;
    }
  }

  // Verificar se é o mesmo estágio (comparar keys)
  let actualFromKey = fromStage;
  if (
    !Object.prototype.hasOwnProperty.call(currentFunnel.value.stages, fromStage)
  ) {
    const fromEntry = Object.entries(currentFunnel.value.stages).find(
      ([, config]) => {
        return config.label === fromStage || config.name === fromStage;
      }
    );
    if (fromEntry) {
      actualFromKey = fromEntry[0];
    }
  }

  if (actualFromKey === actualStageKey) {
    return;
  }

  // Bug #4-5 Fix: Adquirir lock para prevenir conflitos com WebSocket
  const lockKey = `cardMove_${conversationId}`;
  if (isLocked(lockKey)) {
    // Operação já em andamento para este card
    return;
  }

  const release = await acquireLock(lockKey, 10000);

  try {
    // Verificar se o usuário pode mover conversas neste funil
    if (currentFunnel.value?.settings?.allow_manual_move === false) {
      useAlert('Movimentação manual não permitida neste funil');
      return;
    }

    // Encontrar a conversa - pode estar em conversations (Vuex) ou kanbanStagesData (API otimizada)
    let conversation = conversations.value.find(c => c.id === conversationId);

    // Se não encontrou em conversations, procurar em kanbanStagesData
    if (!conversation && kanbanStagesData.value?.stages) {
      const stages = kanbanStagesData.value.stages;
      const foundInStage = stages.find(stageData =>
        stageData.conversations?.some(c => c.id === conversationId)
      );
      if (foundInStage) {
        conversation = foundInStage.conversations.find(
          c => c.id === conversationId
        );
      }
    }

    // Se ainda não encontrou, criar objeto mínimo para permitir o move
    if (!conversation) {
      conversation = { id: conversationId };
    }

    // PERFORMANCE: Track optimistic update (minimal data)
    optimisticUpdates.set(conversationId, {
      previousStage: modifiedStages.value[conversationId] || actualFromKey,
    });

    // INSTANT: Update local state immediately
    modifiedStages.value[conversationId] = actualStageKey;

    // ASYNC: Save to localStorage (non-blocking)
    saveStages({ [conversationId]: actualStageKey });

    // PERFORMANCE: Fire-and-forget API call - don't wait for response
    // The optimistic update already shows the card in the new position
    KanbanAPI.moveConversation(
      currentFunnel.value.id,
      conversationId,
      actualStageKey
    )
      .then(() => {
        // Success - clear optimistic update tracking
        optimisticUpdates.delete(conversationId);

        // PERFORMANCE FIX: Do NOT refetch all conversations after move
        // The optimistic update already shows the correct state
        // WebSocket will sync any other changes if needed
      })
      .catch(error => {
        // Rollback on error
        const optimistic = optimisticUpdates.get(conversationId);
        if (optimistic) {
          modifiedStages.value[conversationId] = optimistic.previousStage;
          saveStages({ [conversationId]: optimistic.previousStage });
          optimisticUpdates.delete(conversationId);
        }

        // Handle 404 - conversation deleted
        if (error?.response?.status === 404) {
          const index = conversations.value.findIndex(
            c => c.id === conversationId
          );
          if (index !== -1) {
            conversations.value.splice(index, 1);
          }
          delete modifiedStages.value[conversationId];
          useAlert('Conversa não encontrada. Removida da visualização.');
          return;
        }

        useAlert('Erro ao mover conversa. Mudança revertida.');
      });

    // Emit event immediately (don't wait for API)
    window.dispatchEvent(
      new CustomEvent('conversation-stage-changed', {
        detail: {
          conversationId,
          fromStage: actualFromKey,
          toStage: actualStageKey,
          funnelId: currentFunnel.value.id,
        },
      })
    );

    // REMOVED: No more fetchConversations() after each move
    // The optimistic update is sufficient for a smooth UX
  } catch {
    // Error handled silently
  } finally {
    // Bug #4-5 Fix: Sempre liberar o lock
    release();
  }
};

const handleCardRemove = async conversation => {
  try {
    // Buscar o mapping da conversa com o funil atual
    const mapping = conversation.funnel_mappings?.find(
      m => String(m.funnel_id) === String(currentFunnel.value.id)
    );

    if (!mapping) {
      useAlert('Erro ao remover do funil');
      return;
    }

    // INSTANT: Remover da lista local imediatamente (UI otimista)
    const index = conversations.value.findIndex(c => c.id === conversation.id);
    if (index !== -1) {
      conversations.value.splice(index, 1);
    }

    // Remover dos stages modificados
    delete modifiedStages.value[conversation.id];

    // ASYNC: Chamar API em background (não esperar)
    ConversationFunnelMappingsAPI.removeFromFunnel(conversation.id, mapping.id)
      .then(() => {
        // Sucesso - card já foi removido da UI
        useAlert(`Conversa removida do funil "${currentFunnel.value.name}"`);
      })
      .catch(() => {
        // Erro - recarregar para sincronizar
        useAlert('Erro ao remover do funil.');
        fetchConversations();
      });

    // PERFORMANCE FIX: NÃO recarregar todas as conversas após remover
    // A remoção local já é suficiente, WebSocket sincroniza se necessário
  } catch (error) {
    useAlert('Erro ao remover do funil. Tente novamente.');
  }
};

// Event listeners
const handleFunnelChange = async event => {
  // Recarregar conversas para o novo funil
  if (event.detail.newFunnelId) {
    await fetchConversations();
  }
};

// Ref para armazenar o intervalo
const refreshInterval = ref(null);

// Auto-recarregar conversas periodicamente
const startAutoRefresh = () => {
  // Bug #31 Fix: Clear any existing interval before starting new one
  if (refreshInterval.value) {
    clearInterval(refreshInterval.value);
  }

  // Recarregar conversas a cada 60 segundos (reduzido para evitar flickering)
  // WebSocket já faz atualizações em tempo real
  refreshInterval.value = setInterval(() => {
    // Bug #31 Fix: Check if component is still mounted before fetching
    if (
      isMounted.value &&
      currentFunnel.value &&
      !isFetching.value &&
      !isLocked('fetchConversations')
    ) {
      fetchConversations();
    }
  }, 60000);
};

// Listener para conversas adicionadas ao funil (definido fora para ser acessível no cleanup)
const handleConversationAddedToFunnel = event => {
  const { conversationId, funnelId, stageName, mapping } = event.detail;

  // Se é do funil atual, atualizar conversas
  if (String(funnelId) === String(currentFunnel.value?.id)) {
    // Buscar se a conversa já existe localmente
    const existingConv = conversations.value.find(c => c.id === conversationId);

    if (existingConv) {
      // Atualizar conversa existente
      // NÃO atualizar stage_name globalmente
      if (existingConv.funnel_mappings) {
        existingConv.funnel_mappings.push(mapping);
      } else {
        existingConv.funnel_mappings = [mapping];
      }

      // Atualizar stage modificado
      modifiedStages.value[conversationId] = stageName;

      // Forçar re-renderização
      nextTick(() => {});
    } else {
      // Conversa não existe, recarregar todas
      fetchConversations();
    }
  }
};

// Listener para conversas removidas do funil (definido fora para ser acessível no cleanup)
const handleConversationRemovedFromFunnel = event => {
  const { funnelId } = event.detail;

  // Se é do funil atual, recarregar conversas
  if (String(funnelId) === String(currentFunnel.value?.id)) {
    fetchConversations();
  }
};

// Listener para atualizações de mapeamento de funil
const handleFunnelMappingUpdate = event => {
  const { conversation_id, funnel_id, stage_name, mapping } = event.detail;

  // Verificar se é do funil atual
  if (String(funnel_id) === String(currentFunnel.value?.id)) {
    const conv = conversations.value.find(c => c.id === conversation_id);
    if (conv) {
      // NÃO atualizar stage_name globalmente - apenas registrar para este funil

      // Atualizar funnel_mappings
      if (conv.funnel_mappings) {
        const existingMapping = conv.funnel_mappings.find(
          m => String(m.funnel_id) === String(funnel_id)
        );
        if (existingMapping) {
          Object.assign(existingMapping, mapping);
        } else {
          conv.funnel_mappings.push(mapping);
        }
      } else {
        conv.funnel_mappings = [mapping];
      }

      // Bug #4-5 & #7 Fix: Update stage optimistically
      modifiedStages.value[conversation_id] = stage_name;

      // Bug #7 Fix: Don't directly write to localStorage
      // Backend and Vuex are the source of truth

      // Forçar re-renderização
      nextTick(() => {});
    } else {
      // Se não encontrou a conversa, recarregar
      fetchConversations();
    }
  }
};

// Listener para atualizações de stage da conversa
const handleConversationStageUpdate = event => {
  const { conversation, stage_name, funnel_id } = event.detail;

  // Verificar se é do funil atual
  if (funnel_id && String(funnel_id) === String(currentFunnel.value?.id)) {
    const conv = conversations.value.find(c => c.id === conversation.id);
    if (conv) {
      // Atualizar conversa local
      Object.assign(conv, conversation);
      conv.stage_name = stage_name;

      // Bug #4-5 & #7 Fix: Update stage optimistically
      modifiedStages.value[conversation.id] = stage_name;

      // Bug #7 Fix: Don't directly write to localStorage
      // Backend and Vuex are the source of truth

      // Forçar re-renderização
      nextTick(() => {});
    }
  }
};

// Helper to setup WebSocket (non-blocking)
const setupWebSocket = () => {
  const accountId = store.getters.getCurrentAccountId;
  if (!accountId || !window.chatwootPubsubToken) return;

  import('shared/helpers/BaseActionCableConnector')
    .then(({ default: BaseConnector }) => {
      const websocketURL = window.chatwootConfig?.websocketURL || '';
      const pubsubToken = window.chatwootPubsubToken;
      const cable = new BaseConnector({}, pubsubToken, websocketURL);
      const channelName = `account_${accountId}_funnel_updates`;

      cable.subscribe(channelName, {
        received: data => {
          if (data.event === 'stage_changed' && data.data) {
            const mappingData = data.data;
            if (
              String(mappingData.funnel_id) === String(currentFunnel.value?.id)
            ) {
              const conv = conversations.value.find(
                c =>
                  c.id === mappingData.conversation_id ||
                  c.display_id === mappingData.conversation_id
              );
              if (conv) {
                conv.stage_name = mappingData.stage_name;
                if (conv.funnel_mappings) {
                  const mapping = conv.funnel_mappings.find(
                    m => String(m.funnel_id) === String(mappingData.funnel_id)
                  );
                  if (mapping) {
                    mapping.stage_name = mappingData.stage_name;
                    mapping.stage_label = mappingData.stage_label;
                    mapping.stage_color = mappingData.stage_color;
                    mapping.stage_entered_at = mappingData.stage_entered_at;
                  }
                }
                nextTick(() => {});
              } else {
                fetchConversations();
              }
            }
          } else if (
            ['mapping_created', 'stage_moved', 'mapping_removed'].includes(
              data.event
            ) &&
            data.data
          ) {
            if (
              String(data.data.funnel_id) === String(currentFunnel.value?.id)
            ) {
              fetchConversations();
            }
          }
        },
      });
      window.kanbanFunnelCable = cable;
    })
    .catch(() => {
      startAutoRefresh();
    });
};

// Helper to setup emitter (non-blocking)
const setupEmitter = () => {
  import('shared/helpers/mitt').then(({ emitter }) => {
    emitter.on('conversation:stage:changed', data => {
      if (
        data.funnelId &&
        String(data.funnelId) === String(currentFunnel.value?.id)
      ) {
        const conv = conversations.value.find(
          c => c.id === data.conversationId
        );
        if (conv) {
          conv.stage_name = data.stageName;
          if (data.conversation) {
            Object.assign(conv, data.conversation);
          }
        }
        modifiedStages.value[data.conversationId] = data.stageName;
      }
    });
  });
};

// Lifecycle - OPTIMIZED for fast loading
onMounted(async () => {
  // Bug #31 Fix: Reset state on mount to ensure clean start
  isMounted.value = true;
  abortController = null;
  isInitializing.value = true;

  // STEP 1: Setup event listeners immediately (non-blocking)
  window.addEventListener('funnel-mapping-updated', handleFunnelMappingUpdate);
  window.addEventListener(
    'conversation-stage-updated',
    handleConversationStageUpdate
  );
  window.addEventListener(
    'conversation-added-to-funnel',
    handleConversationAddedToFunnel
  );
  window.addEventListener(
    'conversation-removed-from-funnel',
    handleConversationRemovedFromFunnel
  );

  // STEP 2: Setup WebSocket and emitter in background (don't await)
  setupWebSocket();
  setupEmitter();

  // STEP 3: Start auto-refresh
  startAutoRefresh();

  try {
    // STEP 4: Load funnels and inboxes in PARALLEL for faster loading
    await Promise.all([store.dispatch('inboxes/get'), loadFunnels()]);

    // STEP 5: Determine which funnel to select
    const funnelIdFromQuery = route.query.funnel_id;
    let targetFunnelId = funnelIdFromQuery || selectedFunnelId.value;

    // If no funnel selected, find a default
    if (!targetFunnelId && funnels.value.length > 0) {
      let defaultFunnel = funnels.value.find(
        f => f.settings?.is_default === true
      );
      if (!defaultFunnel) {
        defaultFunnel = funnels.value.find(f => f.funnel_type === 'sales');
      }
      if (!defaultFunnel) {
        defaultFunnel = funnels.value[0];
      }
      targetFunnelId = defaultFunnel?.id;
    }

    // STEP 6: Select funnel (this will also fetch conversations)
    if (targetFunnelId) {
      await selectFunnel(targetFunnelId);
    }

    // STEP 7: If still no funnels, use createFunnelFromTemplateId from composable
    // This creates a local funnel without making API calls that might fail
    if (funnels.value.length === 0) {
      try {
        await createFunnelFromTemplateId('sales', 'Funil de Vendas', {
          is_default: true,
        });
        // Funnel is already selected by createFunnelFromTemplateId
      } catch {
        // Silently fail - user will see empty state
      }
    }

    // STEP 8: Load saved stages for current funnel
    if (selectedFunnelId.value) {
      const allSavedStages = loadSavedStages();
      modifiedStages.value = {};
      Object.entries(allSavedStages).forEach(([convId, data]) => {
        if (
          typeof data === 'object' &&
          data.funnel_id === selectedFunnelId.value
        ) {
          modifiedStages.value[convId] = data.stage_name;
        }
      });
    }

    // STEP 9: Check if should open specific conversation
    const openConversationId = localStorage.getItem('kanban_open_conversation');
    if (openConversationId) {
      localStorage.removeItem('kanban_open_conversation');
      setTimeout(() => {
        const conversation = conversations.value.find(
          c => String(c.id) === String(openConversationId)
        );
        if (conversation) {
          openConversationModal(conversation);
        }
      }, 500); // Reduced from 1500ms to 500ms
    }

    // Add funnel-changed listener
    window.addEventListener('funnel-changed', handleFunnelChange);

    isInitializing.value = false;
  } catch {
    isInitializing.value = false;
  }
});

// Bug #14, #15, #18, #31 Fix: Cleanup completo de todos os recursos
onBeforeUnmount(() => {
  // Bug #31 Fix: Mark component as unmounted FIRST to prevent any state updates
  isMounted.value = false;

  // Bug #31 Fix: Cancel any pending API requests
  if (abortController) {
    abortController.abort();
    abortController = null;
  }

  // Cancel any pending Kanban API requests
  cancelPendingKanbanRequests();

  // Fix: Reset funnel manager loading state to prevent stuck state on re-mount
  softResetFunnelManagerState();

  // Bug #31 Fix: Force release all locks to prevent deadlocks on re-mount
  // This is critical - without this, locks remain held after component unmounts
  // causing the Kanban to freeze on subsequent visits
  forceReleaseLocks(['fetchConversations', 'saveStages']);

  // Bug #15 Fix: Remover TODOS os event listeners
  window.removeEventListener('funnel-changed', handleFunnelChange);
  window.removeEventListener(
    'conversation-stage-updated',
    handleConversationStageUpdate
  );
  window.removeEventListener(
    'funnel-mapping-updated',
    handleFunnelMappingUpdate
  );
  window.removeEventListener(
    'conversation-added-to-funnel',
    handleConversationAddedToFunnel
  );
  window.removeEventListener(
    'conversation-removed-from-funnel',
    handleConversationRemovedFromFunnel
  );

  // Bug #18 Fix: Limpar conexão WebSocket completamente
  if (window.kanbanFunnelCable) {
    try {
      // Unsubscribe de todos os canais
      const accountId = store.getters.getCurrentAccountId;
      if (accountId) {
        window.kanbanFunnelCable.unsubscribe(
          `account_${accountId}_funnel_updates`
        );
        window.kanbanFunnelCable.unsubscribe(
          `account_${accountId}_conversations`
        );
      }
      // Desconectar completamente
      window.kanbanFunnelCable.disconnect();
    } catch {
      // Error handled silently
    } finally {
      window.kanbanFunnelCable = null;
    }
  }

  // Bug #14 Fix: Limpar interval de auto-refresh
  if (refreshInterval.value) {
    clearInterval(refreshInterval.value);
    refreshInterval.value = null;
  }

  // Bug #14 Fix: Limpar debounce timers
  if (fetchDebounceTimer) {
    clearTimeout(fetchDebounceTimer);
    fetchDebounceTimer = null;
  }

  // Bug #14 Fix: Limpar qualquer fetch timeout pendente
  if (fetchTimeout.value) {
    clearTimeout(fetchTimeout.value);
    fetchTimeout.value = null;
  }

  // Bug #30 Fix: Limpar store update debounce timer
  if (storeUpdateDebounceTimer) {
    clearTimeout(storeUpdateDebounceTimer);
    storeUpdateDebounceTimer = null;
  }
});

// Watch para recarregar conversas quando trocar de funil
// Bug #32 Fix: Use shallow watch on ID only to prevent infinite loops from deep object changes
watch(
  () => currentFunnel.value?.id,
  async (newFunnelId, oldFunnelId) => {
    // Bug #32 Fix: Skip if IDs are the same (prevents loop from deep object mutations)
    if (!newFunnelId || newFunnelId === oldFunnelId) return;

    const newFunnel = currentFunnel.value;
    if (!newFunnel) return;

    // Bug #14 Fix: Clear interval when switching funnels to prevent memory leak
    if (refreshInterval.value) {
      clearInterval(refreshInterval.value);
      refreshInterval.value = null;
    }

    // Limpar estágios modificados do funil anterior
    modifiedStages.value = {};

    // Carregar stages salvos ESPECÍFICOS do novo funil
    const allSavedStages = loadSavedStages();
    const funnelStages = {};

    // Filtrar apenas os stages do novo funil
    Object.entries(allSavedStages).forEach(([convId, data]) => {
      if (typeof data === 'object' && data.funnel_id === newFunnelId) {
        funnelStages[convId] = data.stage;
      }
    });

    // Aplicar os stages carregados
    modifiedStages.value = funnelStages;

    // Resetar filtros avançados ao trocar de funil
    appliedFilter.value = [];
    store.dispatch('clearConversationFilters');

    // Buscar conversas com filtros do novo funil
    fetchConversations();

    // Bug #14 Fix: Restart auto-refresh for the new funnel
    startAutoRefresh();
  },
  { immediate: false }
);

// Watch para recarregar quando mudar filtros básicos - COM PROTEÇÃO
watch([activeStatus, activeSortBy, activeAssigneeTab], () => {
  // Cancelar timer anterior se existir
  if (fetchDebounceTimer) {
    clearTimeout(fetchDebounceTimer);
  }

  // Aguardar 300ms antes de buscar conversas (debounce)
  fetchDebounceTimer = setTimeout(() => {
    // Reset tentativas ao mudar filtros manualmente
    fetchAttempts.value = 0;

    nextTick(() => {
      if (!isFetching.value) {
        fetchConversations();
      }
    });
  }, 300);
});

// DISABLED: Watch REAL-TIME para mudanças no store de conversas
// This was causing conflicts with the optimized Kanban API
// The kanbanStagesData is now the source of truth, not the Vuex store
// WebSocket updates are handled by event listeners instead
//
// watchEffect(() => {
//   const allConversations = store.state.conversations?.allConversations || [];
//   if (allConversations.length === 0) return;
//   ... (disabled to prevent flickering and data conflicts)
// });
</script>

<template>
  <div class="kanban-container flex flex-col h-full w-full overflow-hidden">
    <!-- Header Kanban - Layout: Esquerda | Centro | Direita -->
    <div class="sticky top-0 z-50 bg-n-solid-2 border-b border-n-weak">
      <div class="flex items-center justify-between px-[30px] py-5">
        <!-- ESQUERDA: Search + Filtro + Avatares -->
        <div class="flex items-center gap-3">
          <!-- Campo de Pesquisa -->
          <div class="relative">
            <input
              v-model="searchQuery"
              type="text"
              placeholder="Pesquisar item"
              class="no-margin w-52 h-10 pl-9 pr-3 bg-n-solid-3 border border-n-weak rounded-lg text-sm text-n-slate-12 placeholder-n-slate-10 focus:outline-none focus:border-woot-500 focus:ring-1 focus:ring-woot-500"
            />
            <fluent-icon
              icon="search"
              size="14"
              class="absolute left-3 top-1/2 -translate-y-1/2 text-n-slate-10 pointer-events-none"
            />
          </div>

          <!-- Botão Filtro -->
          <div class="flex items-center gap-1">
            <button
              class="flex items-center justify-center w-10 h-10 bg-n-solid-3 border border-n-weak rounded-lg text-n-slate-11 hover:text-n-slate-12 hover:bg-n-alpha-2 hover:border-n-slate-8 transition-colors"
              :class="{
                'border-woot-500 text-woot-400 bg-woot-500/10':
                  hasAppliedFilters,
              }"
              title="Filtrar itens"
              @click="handleFiltersModal"
            >
              <fluent-icon icon="filter" size="16" />
            </button>
            <!-- Botão limpar filtros (aparece quando há filtros) -->
            <button
              v-if="hasAppliedFilters"
              class="flex items-center justify-center w-10 h-10 bg-n-ruby-3 border border-n-ruby-7 rounded-lg text-n-ruby-11 hover:text-n-ruby-12 hover:bg-n-ruby-4 hover:border-n-ruby-8 transition-colors"
              title="Limpar filtros"
              @click="clearAdvancedFilters"
            >
              <fluent-icon icon="dismiss" size="16" />
            </button>
          </div>

          <!-- Avatares dos Agentes -->
          <div class="flex items-center -space-x-2">
            <div
              v-for="(agent, index) in visibleAgents"
              :key="agent.id"
              class="relative"
              :style="{ zIndex: 10 - index }"
            >
              <img
                v-if="agent.avatar_url"
                :src="agent.avatar_url"
                :alt="agent.name"
                :title="agent.name"
                class="w-8 h-8 rounded-full border-2 border-n-solid-2 object-cover cursor-pointer hover:scale-110 transition-transform"
              />
              <div
                v-else
                :title="agent.name"
                class="w-8 h-8 rounded-full border-2 border-n-solid-2 bg-woot-500 text-white text-xs flex items-center justify-center font-medium cursor-pointer hover:scale-110 transition-transform"
              >
                {{ agent.name?.charAt(0) }}
              </div>
            </div>
            <!-- Contador de agentes adicionais -->
            <div
              v-if="additionalAgentsCount > 0"
              class="w-8 h-8 rounded-full border-2 border-n-solid-2 bg-n-alpha-3 text-n-slate-11 text-xs flex items-center justify-center font-medium cursor-pointer"
              title="Ver todos os agentes"
            >
              +{{ additionalAgentsCount }}
            </div>
          </div>
        </div>

        <!-- CENTRO: Valor Total + Calendário -->
        <div class="flex items-center gap-3 h-10">
          <!-- Valor Total -->
          <div
            class="flex items-center justify-center h-10 px-5 min-w-[150px] bg-n-teal-3 border border-n-teal-7 rounded-lg"
          >
            <span class="text-base font-bold text-n-teal-11 leading-none">
              {{ formattedTotalGeneralValue }}
            </span>
          </div>
          <!-- Botão Calendário -->
          <router-link
            :to="{ name: 'calendar' }"
            class="flex items-center gap-2 h-10 px-4 bg-n-solid-3 hover:bg-n-alpha-2 border border-n-weak rounded-lg transition-colors"
            title="Abrir Calendário"
          >
            <fluent-icon icon="calendar" size="18" class="text-n-slate-11" />
            <span class="text-sm font-medium text-n-slate-12">Calendário</span>
          </router-link>
        </div>

        <!-- DIREITA: Contador + Add + Funil + Settings -->
        <div class="flex items-center gap-3 h-10">
          <!-- Contador de Cards -->
          <div
            class="flex items-center gap-2 h-10 px-3 bg-n-solid-3 border border-n-weak rounded-lg"
          >
            <fluent-icon icon="document" size="14" class="text-n-slate-11" />
            <span class="text-sm font-semibold text-n-slate-12 leading-none">
              {{ totalCardsCount }}
            </span>
          </div>

          <!-- Seletor de Funil -->
          <div class="relative flex items-center">
            <button
              class="flex items-center justify-between gap-2 px-3 h-10 bg-n-solid-3 hover:bg-n-alpha-2 border border-n-weak rounded-lg text-sm font-medium text-n-slate-12 transition-colors min-w-[140px]"
              @click="showFunnelDropdown = !showFunnelDropdown"
            >
              <span class="truncate leading-none">{{
                currentFunnel?.name || 'Selecione'
              }}</span>
              <fluent-icon
                icon="chevron-down"
                size="14"
                class="text-n-slate-11 flex-shrink-0"
              />
            </button>
            <!-- Dropdown do Funil -->
            <div
              v-if="showFunnelDropdown"
              v-on-click-outside="() => (showFunnelDropdown = false)"
              class="absolute top-full right-0 mt-1 w-72 bg-n-solid-3 border border-n-weak rounded-lg shadow-xl z-50 overflow-hidden"
            >
              <!-- Tabs Ativos/Arquivados -->
              <div class="flex border-b border-n-weak">
                <button
                  class="flex-1 px-4 py-2.5 text-sm font-medium transition-colors"
                  :class="
                    funnelTab === 'active'
                      ? 'text-woot-400 border-b-2 border-woot-400 bg-n-alpha-2'
                      : 'text-n-slate-11 hover:text-n-slate-12'
                  "
                  @click="funnelTab = 'active'"
                >
                  Ativos
                </button>
                <button
                  class="flex-1 px-4 py-2.5 text-sm font-medium transition-colors"
                  :class="
                    funnelTab === 'archived'
                      ? 'text-woot-400 border-b-2 border-woot-400 bg-n-alpha-2'
                      : 'text-n-slate-11 hover:text-n-slate-12'
                  "
                  @click="funnelTab = 'archived'"
                >
                  Arquivados
                </button>
              </div>
              <!-- Search -->
              <div class="p-2 border-b border-n-weak">
                <div class="relative">
                  <input
                    v-model="funnelSearchQuery"
                    type="text"
                    placeholder="Pesquisar..."
                    class="w-full h-8 pl-8 pr-3 bg-n-solid-2 border border-n-weak rounded-md text-sm text-n-slate-12 placeholder-n-slate-10 focus:outline-none focus:border-woot-500"
                  />
                  <fluent-icon
                    icon="search"
                    size="14"
                    class="absolute left-2.5 top-1/2 -translate-y-1/2 text-n-slate-10"
                  />
                </div>
              </div>
              <!-- Lista de Funis -->
              <div class="max-h-64 overflow-y-auto py-1">
                <button
                  v-for="funnel in filteredFunnels"
                  :key="funnel.id"
                  class="w-full px-4 py-2.5 text-left text-sm hover:bg-n-alpha-2 flex items-center gap-2 transition-colors"
                  :class="
                    funnel.id === selectedFunnelId
                      ? 'bg-n-alpha-2 text-woot-400'
                      : 'text-n-slate-12'
                  "
                  @click="selectFunnelFromDropdown(funnel.id)"
                >
                  <span>{{ funnel.icon || '📋' }}</span>
                  <span class="flex-1 truncate">{{ funnel.name }}</span>
                  <fluent-icon
                    v-if="funnel.id === selectedFunnelId"
                    icon="checkmark"
                    size="14"
                    class="text-woot-400"
                  />
                </button>
                <div
                  v-if="filteredFunnels.length === 0"
                  class="px-4 py-3 text-sm text-n-slate-10 text-center"
                >
                  Nenhum funil encontrado
                </div>
              </div>
            </div>
          </div>

          <!-- Botão Settings -->
          <div class="relative flex items-center">
            <button
              class="flex items-center justify-center w-10 h-10 bg-n-solid-3 border border-n-weak rounded-lg text-n-slate-11 hover:text-n-slate-12 hover:bg-n-alpha-2 transition-colors"
              title="Configurações"
              @click="showSettingsDropdown = !showSettingsDropdown"
            >
              <fluent-icon icon="settings" size="18" />
            </button>
            <!-- Dropdown Settings -->
            <div
              v-if="showSettingsDropdown"
              v-on-click-outside="() => (showSettingsDropdown = false)"
              class="absolute top-full right-0 mt-2 w-56 bg-n-solid-3 border border-n-weak rounded-lg shadow-xl py-1 z-50"
            >
              <button
                class="w-full px-4 py-2.5 text-left text-sm text-n-slate-12 hover:bg-n-alpha-2 flex items-center gap-2"
                @click="handleGerenciarFunil"
              >
                <fluent-icon icon="board" size="16" class="text-n-slate-11" />
                Gerenciar Funil
              </button>
              <button
                class="w-full px-4 py-2.5 text-left text-sm text-n-slate-12 hover:bg-n-alpha-2 flex items-center gap-2"
                @click="handleGerenciarPermissoes"
              >
                <fluent-icon icon="people" size="16" class="text-n-slate-11" />
                Configurações e Permissões
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Loading State ao trocar de funil ou inicializar -->
    <div
      v-if="isLoadingFunnel || isInitializing"
      class="flex-1 flex items-center justify-center"
    >
      <div class="text-center">
        <div
          class="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-3"
        />
        <p class="text-slate-500">
          {{
            isInitializing ? 'Inicializando Kanban...' : 'Carregando funil...'
          }}
        </p>
      </div>
    </div>

    <!-- Debug Info -->
    <!-- eslint-disable vue/no-bare-strings-in-template -->
    <div v-if="false" class="p-4 bg-yellow-100 text-sm">
      <div>currentFunnel: {{ currentFunnel ? 'YES' : 'NO' }}</div>
      <div>isLoadingFunnel: {{ isLoadingFunnel }}</div>
      <div>isInitializing: {{ isInitializing }}</div>
      <div>conversationList.length: {{ conversationList.length }}</div>
    </div>

    <!-- Board Kanban de Conversas -->
    <KanbanBoard
      v-if="
        currentFunnel &&
        !isLoadingFunnel &&
        !isInitializing &&
        conversationList.length >= 0
      "
      :funnel="currentFunnel"
      :grouped-conversations="groupedConversations"
      :stage-loading-more="stageLoadingMore"
      class="flex-1 overflow-x-auto overflow-y-hidden"
      @card-click="handleOpenSidePanel"
      @card-move="handleCardMove"
      @card-remove="handleCardRemove"
      @open-conversation="openConversationModal"
      @open-crm="openCrmModal"
      @load-more="loadMoreStageConversations"
    />

    <!-- Empty State - Funil sem conversas -->
    <div
      v-if="
        currentFunnel &&
        !isLoadingFunnel &&
        !isInitializing &&
        conversationList.length === 0 &&
        false
      "
      class="flex-1 flex items-center justify-center"
    >
      <div class="text-center max-w-md">
        <fluent-icon icon="filter" size="60" class="text-slate-300 mb-4" />
        <h3
          class="text-lg font-semibold text-slate-700 dark:text-slate-300 mb-2"
        >
          Nenhuma conversa neste funil
        </h3>
        <p class="text-slate-500 dark:text-slate-400 mb-4">
          Este funil ainda não tem conversas. Para adicionar conversas:
        </p>
        <ol
          class="text-left text-sm text-slate-600 dark:text-slate-400 space-y-2 mb-4"
        >
          <li class="flex items-start">
            <span class="font-semibold mr-2">1.</span>
            <span>Abra uma conversa na lista de conversas</span>
          </li>
          <li class="flex items-start">
            <span class="font-semibold mr-2">2.</span>
            <span
              >No menu lateral da conversa, clique em "Adicionar ao Funil"</span
            >
          </li>
          <li class="flex items-start">
            <span class="font-semibold mr-2">3.</span>
            <span>Selecione este funil e escolha a etapa inicial</span>
          </li>
        </ol>
        <router-link
          to="/app/accounts/1/dashboard/home"
          class="inline-flex items-center px-4 py-2 bg-woot-500 text-white rounded-lg hover:bg-woot-600"
        >
          <fluent-icon icon="chat" size="16" class="mr-2" />
          Ir para Conversas
        </router-link>
      </div>
    </div>

    <!-- Empty State -->
    <div
      v-else-if="!currentFunnel && !isLoadingFunnel && !isInitializing"
      class="flex-1 flex items-center justify-center"
    >
      <div class="text-center">
        <fluent-icon icon="board" size="60" class="text-slate-300 mb-4" />
        <h3
          class="text-lg font-semibold text-slate-700 dark:text-slate-300 mb-2"
        >
          Nenhum funil selecionado
        </h3>
        <p class="text-slate-500 dark:text-slate-400 mb-4">
          {{
            funnels.length > 0
              ? 'Selecione um funil acima para visualizar as conversas'
              : 'Configurando funis...'
          }}
        </p>
        <div v-if="funnels.length === 0" class="mt-4">
          <div
            class="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"
          />
        </div>
      </div>
    </div>

    <!-- Modal de Filtros Avançados -->
    <Teleport to="body">
      <div
        v-if="showAdvancedFilters"
        class="fixed inset-0 z-[9999] flex items-start justify-center pt-16"
      >
        <!-- Backdrop -->
        <div
          class="absolute inset-0 bg-n-black/80 backdrop-blur-sm"
          @click="closeAdvanceFiltersModal"
        />
        <!-- Modal Content -->
        <div class="relative z-10 w-full max-w-2xl mx-4">
          <ConversationFilter
            v-model="appliedFilter"
            folder-name=""
            :is-folder-view="false"
            @apply-filter="onApplyFilter"
            @close="closeAdvanceFiltersModal"
          />
        </div>
      </div>
    </Teleport>

    <!-- Modal de Conversa -->
    <ConversationModal
      v-if="showConversationModal"
      :conversation-id="selectedConversationId"
      @close="showConversationModal = false"
    />

    <!-- Modal CRM -->
    <CrmModal
      v-if="showCrmModal && selectedContactId"
      :contact-id="selectedContactId"
      :conversation-id="selectedContactConversationId"
      @close="closeCrmModal"
      @contact-updated="handleContactUpdated"
    />

    <!-- Painel Lateral com tabs -->
    <KanbanSidePanel
      :conversation="selectedConversation"
      :is-open="showSidePanel"
      :stage-color="selectedConversationStageColor"
      @close="closeSidePanel"
      @refresh="handleSidePanelRefresh"
    />

  </div>
</template>

<style scoped>
/* Select customizado do Funil */
select option {
  padding: 8px 12px;
  border-radius: 4px;
  background-color: var(--n-solid-1);
  color: var(--n-slate-12);
  font-size: 14px;
  line-height: 1.5;
}

select option:hover {
  background-color: var(--n-alpha-2);
}

select option:checked {
  background-color: var(--n-blue-3);
  color: var(--n-blue-text);
  font-weight: 500;
}

/* Estilizar o dropdown no Chrome/Safari */
select::-webkit-scrollbar {
  width: 8px;
}

select::-webkit-scrollbar-track {
  background: var(--n-solid-2);
  border-radius: 4px;
}

select::-webkit-scrollbar-thumb {
  background: var(--n-slate-8);
  border-radius: 4px;
}

select::-webkit-scrollbar-thumb:hover {
  background: var(--n-slate-9);
}
</style>
